import React, { useState, useEffect } from 'react';
import { linkify } from '../../utils/linkify.tsx';

// Helper function to format date string without timezone conversion
const formatDateSafe = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  maxAttendees: number;
  currentAttendees: number;
  rsvpEnabled: boolean;
  rsvpDeadline?: string;
  color: string;
  createdBy: string;
  createdAt: string;
  rsvps: any[];
}

interface RSVPData {
  familyId: string;
  status: 'attending' | 'not_attending';
  ranchGirls: string[];
  respondedAt: string;
}

interface Attendee {
  firstName: string;
  lastName: string;
}

export default function EventCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [showAllEventsModal, setShowAllEventsModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [rsvpStatus, setRsvpStatus] = useState<'attending' | 'not_attending'>('attending');
  const [attendees, setAttendees] = useState<Attendee[]>([{ firstName: '', lastName: '' }]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/events?upcoming=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setEvents(data.data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };



  const handleRSVP = async () => {
    if (!selectedEvent) return;

    // Validate attendees if attending
    if (rsvpStatus === 'attending') {
      const validAttendees = attendees.filter(a => a.firstName.trim() && a.lastName.trim());
      if (validAttendees.length === 0) {
        alert('Please add at least one attendee with first and last name');
        return;
      }
    }

    try {
      const token = localStorage.getItem('authToken');
      const validAttendees = attendees.filter(a => a.firstName.trim() && a.lastName.trim());
      
      const response = await fetch(`/api/events/${selectedEvent.id}/rsvp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: rsvpStatus,
          attendees: rsvpStatus === 'attending' ? validAttendees : []
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('RSVP updated successfully!');
        setShowRSVPModal(false);
        setSelectedEvent(null);
        fetchEvents(); // Refresh events to show updated RSVP count
      } else {
        alert('Error updating RSVP: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      alert('Error updating RSVP');
    }
  };

  const openRSVPModal = (event: Event) => {
    setSelectedEvent(event);
    setShowRSVPModal(true);
    
    // Check if family already has an RSVP for this event
    const existingRSVP = getFamilyRSVP(event);
    if (existingRSVP) {
      setRsvpStatus(existingRSVP.status);
      // Load existing attendees if available
      if (existingRSVP.attendees && existingRSVP.attendees.length > 0) {
        setAttendees(existingRSVP.attendees);
      } else {
        setAttendees([{ firstName: '', lastName: '' }]);
      }
    } else {
      setRsvpStatus('attending');
      setAttendees([{ firstName: '', lastName: '' }]);
    }
  };

  const addAttendee = () => {
    setAttendees([...attendees, { firstName: '', lastName: '' }]);
  };

  const removeAttendee = (index: number) => {
    if (attendees.length > 1) {
      setAttendees(attendees.filter((_, i) => i !== index));
    }
  };

  const updateAttendee = (index: number, field: 'firstName' | 'lastName', value: string) => {
    const newAttendees = [...attendees];
    newAttendees[index][field] = value;
    setAttendees(newAttendees);
  };

  // Function to check if family/alumni has RSVP'd for an event
  const getFamilyRSVP = (event: Event) => {
    const authUser = localStorage.getItem('authUser');
    if (!authUser) return null;
    
    try {
      const user = JSON.parse(authUser);
      
      // Check if user is alumni or family and find their RSVP
      if (user.role === 'alumni' && user.alumniId) {
        return event.rsvps.find(rsvp => rsvp.alumniId === user.alumniId);
      } else if (user.role === 'family' && user.familyId) {
        return event.rsvps.find(rsvp => rsvp.familyId === user.familyId);
      }
    } catch (error) {
      console.error('Error parsing authUser:', error);
    }
    
    return null;
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getEventsForDate = (day: number) => {
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days: JSX.Element[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-24 bg-gray-50"></div>
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isToday = dayDate.toDateString() === today.toDateString();
      const isPast = dayDate < today;
      const dayEvents = getEventsForDate(day);

      days.push(
        <div
          key={day}
          className={`h-24 border border-gray-200 p-1 ${
            isPast 
              ? 'bg-gray-100' 
              : 'bg-white'
          } ${isToday ? 'bg-ggaa-green/10 border-ggaa-green' : ''}`}
        >
          <div className={`text-sm font-medium ${isPast ? 'text-gray-400' : 'text-gray-900'} ${isToday ? 'text-ggaa-green' : ''}`}>
            {day}
          </div>
          <div className="mt-1 space-y-1">
            {dayEvents.slice(0, 2).map(event => (
              <div
                key={event.id}
                className="text-xs text-white px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80"
                style={{ backgroundColor: event.color || '#16a34a' }}
                onClick={() => openRSVPModal(event)}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div 
                className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDayEvents(dayEvents);
                  setShowAllEventsModal(true);
                }}
              >
                +{dayEvents.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Get events for the currently displayed month
  const getEventsForMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
    return events
      .filter(event => event.date.startsWith(monthStr))
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  };

  // Check if a day has events (for mobile mini calendar dots)
  const dayHasEvents = (day: number) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.some(event => event.date === dateStr);
  };

  return (
    <div className="px-4 py-6 md:px-12 md:py-10">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">Event Calendar</h1>
        <p className="text-sm md:text-base text-gray-600">Click on an event to view details and RSVP</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
          <span className="ml-3 text-gray-600">Loading events...</span>
        </div>
      ) : (
        <>
          {/* Desktop Calendar - hidden on mobile */}
          <div className="hidden md:block bg-white rounded-xl shadow-lg p-6 mb-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ←
              </button>
              <h3 className="text-lg font-semibold text-gray-900">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                →
              </button>
            </div>

            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-0 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="h-8 flex items-center justify-center text-sm font-medium text-gray-500 bg-gray-50">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-0 border border-gray-200">
              {renderCalendar()}
            </div>
          </div>

          {/* Mobile Calendar - compact mini calendar + event list */}
          <div className="md:hidden bg-white rounded-xl shadow-lg p-4 mb-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ←
              </button>
              <h3 className="text-base font-semibold text-gray-900">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                →
              </button>
            </div>

            {/* Mini Calendar Grid */}
            <div className="grid grid-cols-7 gap-0 mb-3">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="h-6 flex items-center justify-center text-xs font-medium text-gray-400">
                  {day}
                </div>
              ))}
              {(() => {
                const daysInMonth = getDaysInMonth(currentDate);
                const firstDay = getFirstDayOfMonth(currentDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const cells: JSX.Element[] = [];
                for (let i = 0; i < firstDay; i++) {
                  cells.push(<div key={`me-${i}`} className="h-8"></div>);
                }
                for (let day = 1; day <= daysInMonth; day++) {
                  const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const isToday = dayDate.toDateString() === today.toDateString();
                  const hasEvents = dayHasEvents(day);
                  cells.push(
                    <div key={day} className="h-8 flex flex-col items-center justify-center">
                      <span className={`text-xs font-medium leading-none w-7 h-7 flex items-center justify-center rounded-full ${
                        isToday ? 'bg-ggaa-green text-white' :
                        hasEvents ? 'bg-green-100 text-green-800 font-semibold ring-2 ring-green-400' :
                        'text-gray-700'
                      }`}>
                        {day}
                      </span>
                    </div>
                  );
                }
                return cells;
              })()}
            </div>

            {/* Mobile Month Events List */}
            {(() => {
              const monthEvents = getEventsForMonth();
              if (monthEvents.length === 0) {
                return <p className="text-gray-400 text-center text-sm py-3">No events this month</p>;
              }
              return (
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  {monthEvents.map(event => {
                    const familyRSVP = getFamilyRSVP(event);
                    const isRSVPd = familyRSVP && familyRSVP.status === 'attending';
                    return (
                      <div
                        key={event.id}
                        className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                        onClick={() => openRSVPModal(event)}
                      >
                        <div
                          className="w-1 self-stretch rounded-full flex-shrink-0"
                          style={{ backgroundColor: event.color || '#16a34a' }}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-gray-900 truncate">{event.title}</span>
                            {isRSVPd && <span className="text-xs text-green-600">✓</span>}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatDateSafe(event.date)} {event.time !== 'TBD' ? `at ${event.time}` : '· Time TBD'}
                          </div>
                        </div>
                        {event.rsvpEnabled && (
                          <span className="text-xs text-ggaa-green flex-shrink-0">RSVP →</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Upcoming Events List */}
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
            {events.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No upcoming events</p>
            ) : (
              <div className="space-y-4">
                {events.map(event => {
                  const familyRSVP = getFamilyRSVP(event);
                  const isRSVPd = familyRSVP && familyRSVP.status === 'attending';
                  const isDeclined = familyRSVP && familyRSVP.status === 'not_attending';
                  
                  return (
                    <div key={event.id} className={`p-4 border rounded-lg ${
                      isRSVPd ? 'border-green-300 bg-green-50' : 
                      isDeclined ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-gray-900">{event.title}</h4>
                            {isRSVPd && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ RSVP'd
                              </span>
                            )}
                            {isDeclined && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                ✗ Declined
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{linkify(event.description)}</p>
                          <p className="text-sm text-gray-500 mt-2">
                            📅 {formatDateSafe(event.date)} at {event.time}
                          </p>
                          <p className="text-sm text-gray-500">📍 {event.location}</p>
                          <p className="text-sm text-gray-500">
                            👥 {event.currentAttendees}/{event.maxAttendees} attendees
                          </p>
                          {isRSVPd && familyRSVP.attendeeCount > 0 && (
                            <p className="text-sm text-green-600 font-medium mt-1">
                              Your party: {familyRSVP.attendeeCount} attending
                              {familyRSVP.attendees && familyRSVP.attendees.length > 0 && (
                                <span className="text-xs block text-gray-600 mt-0.5">
                                  ({familyRSVP.attendees.map((a: any) => `${a.firstName} ${a.lastName}`).join(', ')})
                                </span>
                              )}
                            </p>
                          )}
                          {event.rsvpDeadline && (
                            <p className="text-sm text-yellow-600 mt-1">
                              RSVP by: {formatDateSafe(event.rsvpDeadline)}
                            </p>
                          )}
                        </div>
                        <div className="ml-4">
                          {event.rsvpEnabled && (
                            <button
                              onClick={() => openRSVPModal(event)}
                              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                                isRSVPd ? 'bg-green-600 text-white hover:bg-green-700' :
                                isDeclined ? 'bg-gray-600 text-white hover:bg-gray-700' :
                                'bg-ggaa-green text-white hover:bg-ggaa-green/90'
                              }`}
                            >
                              {familyRSVP ? 'Update RSVP' : 'RSVP'}
                            </button>
                          )}
                          {!event.rsvpEnabled && (
                            <span className="text-sm text-gray-500">No RSVP required</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* All Events Modal */}
      {showAllEventsModal && selectedDayEvents.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Events on {selectedDayEvents.length > 0 && formatDateSafe(selectedDayEvents[0].date)}
              </h3>
              <button
                onClick={() => {
                  setShowAllEventsModal(false);
                  setSelectedDayEvents([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              {selectedDayEvents.map(event => {
                const familyRSVP = getFamilyRSVP(event);
                const isRSVPd = familyRSVP && familyRSVP.status === 'attending';
                const isDeclined = familyRSVP && familyRSVP.status === 'not_attending';

                return (
                  <div 
                    key={event.id} 
                    className={`p-4 border rounded-lg ${
                      isRSVPd ? 'border-green-300 bg-green-50' : 
                      isDeclined ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: event.color || '#16a34a' }}
                          ></div>
                          <h4 className="font-medium text-gray-900">{event.title}</h4>
                          {isRSVPd && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✓ RSVP'd
                            </span>
                          )}
                          {isDeclined && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ✗ Declined
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{linkify(event.description)}</p>
                        <p className="text-sm text-gray-500 mt-2">
                          🕒 {event.time}
                        </p>
                        <p className="text-sm text-gray-500">📍 {event.location}</p>
                        <p className="text-sm text-gray-500">
                          👥 {event.currentAttendees}/{event.maxAttendees} attendees
                        </p>
                      </div>
                      <div className="ml-4">
                        {event.rsvpEnabled && (
                          <button
                            onClick={() => {
                              setShowAllEventsModal(false);
                              openRSVPModal(event);
                            }}
                            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                              isRSVPd ? 'bg-green-600 text-white hover:bg-green-700' :
                              isDeclined ? 'bg-gray-600 text-white hover:bg-gray-700' :
                              'bg-ggaa-green text-white hover:bg-ggaa-green/90'
                            }`}
                          >
                            {familyRSVP ? 'Update RSVP' : 'RSVP'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setShowAllEventsModal(false);
                  setSelectedDayEvents([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RSVP Modal */}
      {showRSVPModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50 md:p-4">
          <div className="bg-white rounded-t-xl md:rounded-xl p-4 md:p-6 w-full max-w-2xl md:mx-4 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base md:text-lg font-semibold text-gray-900">RSVP for {selectedEvent.title}</h3>
              <button
                onClick={() => setShowRSVPModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                📅 {formatDateSafe(selectedEvent.date)} {selectedEvent.time !== 'TBD' ? `at ${selectedEvent.time}` : '· Time TBD'}
              </p>
              <p className="text-sm text-gray-600">📍 {selectedEvent.location}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Will you be attending?
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="rsvpStatus"
                    value="attending"
                    checked={rsvpStatus === 'attending'}
                    onChange={(e) => setRsvpStatus(e.target.value as 'attending' | 'not_attending')}
                    className="mr-2"
                  />
                  Yes, I'll be attending
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="rsvpStatus"
                    value="not_attending"
                    checked={rsvpStatus === 'not_attending'}
                    onChange={(e) => setRsvpStatus(e.target.value as 'attending' | 'not_attending')}
                    className="mr-2"
                  />
                  No, I can't attend
                </label>
              </div>
            </div>

            {rsvpStatus === 'attending' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Who will be attending?
                </label>
                <div className="space-y-3">
                  {attendees.map((attendee, index) => (
                    <div key={index} className="flex flex-col sm:flex-row gap-2 p-3 bg-gray-50 rounded-lg relative">
                      <input
                        type="text"
                        placeholder="First Name"
                        value={attendee.firstName}
                        onChange={(e) => updateAttendee(index, 'firstName', e.target.value)}
                        className="w-full sm:flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={attendee.lastName}
                        onChange={(e) => updateAttendee(index, 'lastName', e.target.value)}
                        className="w-full sm:flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent text-sm"
                      />
                      {attendees.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeAttendee(index)}
                          className="absolute top-1 right-1 sm:static p-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs"
                          title="Remove attendee"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addAttendee}
                  className="mt-3 text-sm text-ggaa-green hover:text-ggaa-green/80 font-medium"
                >
                  + Add Another Attendee
                </button>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setShowRSVPModal(false)}
                className="px-4 py-2.5 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleRSVP}
                className="px-4 py-2.5 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 transition-colors text-sm"
              >
                Submit RSVP
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 